import { t } from '../../i18n';
import {
  clearAnalyticsConsent,
  getAnalyticsConsent,
  setAnalyticsConsent,
} from '../../utils/analytics-consent-storage';
import { loadAnalyticsIfConsented } from '../../utils/load-analytics';
import { flushPendingMetaEvents } from '../../utils/meta-events';
import { applyGoogleConsent } from '../../utils/google-consent';
import { refreshAds } from '../../features/ads';

const BANNER_ID = 'analytics-consent-banner';

function buildConsentBanner(): string {
  return /* html */ `
<div id="${BANNER_ID}" class="consent-banner" role="dialog" aria-live="polite" aria-label="${t('consent.title')}">
  <div class="consent-banner__content">
    <p class="consent-banner__title">${t('consent.title')}</p>
    <p class="consent-banner__message">
      ${t('consent.message')}
      <button type="button" class="consent-banner__link" data-consent-policy>
        ${t('consent.policy')}
      </button>
    </p>
  </div>
  <div class="consent-banner__actions">
    <button type="button" class="consent-banner__button consent-banner__button--secondary" data-consent-reject>
      ${t('consent.reject')}
    </button>
    <button type="button" class="consent-banner__button consent-banner__button--primary" data-consent-accept>
      ${t('consent.accept')}
    </button>
  </div>
</div>
`;
}

function getBanner(): HTMLElement | null {
  return document.getElementById(BANNER_ID);
}

function removeBanner(): void {
  getBanner()?.remove();
}

function bindConsentBanner(): void {
  const banner = getBanner();
  if (!banner) return;

  banner.querySelector('[data-consent-policy]')?.addEventListener('click', () => {
    window.openCookieModal?.();
  });

  banner.querySelector('[data-consent-reject]')?.addEventListener('click', () => {
    setAnalyticsConsent('denied');
    applyGoogleConsent(false);
    removeBanner();
  });

  banner.querySelector('[data-consent-accept]')?.addEventListener('click', () => {
    setAnalyticsConsent('granted');
    applyGoogleConsent(true);
    loadAnalyticsIfConsented();
    flushPendingMetaEvents();
    refreshAds();
    removeBanner();
  });
}

function renderConsentBanner(): void {
  removeBanner();
  document.body.insertAdjacentHTML('beforeend', buildConsentBanner());
  bindConsentBanner();
}

export function initConsentBanner(): void {
  const consent = getAnalyticsConsent();

  if (consent === 'granted') {
    applyGoogleConsent(true);
    loadAnalyticsIfConsented();
    refreshAds();
    return;
  }

  if (consent === null) {
    renderConsentBanner();
  }
}

export function remountConsentBanner(): void {
  if (getBanner()) {
    renderConsentBanner();
  }
}

export function reopenCookieConsent(): void {
  clearAnalyticsConsent();
  renderConsentBanner();
}
